import React, { useId, useMemo } from 'react';

export type WebDonutSlice = {
  amount: number;
  color: string;
  name?: string;
};

type Props = {
  data: WebDonutSlice[];
  size?: number;
  strokeWidth?: number;
  /** Écart entre segments (degrés). Si omis, calculé selon l’épaisseur pour éviter les chevauchements. */
  gapDegrees?: number;
  centerPrimary: string;
  centerSecondary?: string;
  /** Au-delà, les petites parts sont regroupées en « Autres ». */
  maxSlices?: number;
};

function polar(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number,
) {
  // Garde un écart min pour éviter arcs dégénérés / bugs SVG
  const sweep = Math.max(0.01, endAngle - startAngle);
  const end = startAngle + Math.min(sweep, 359.99);
  const start = polar(cx, cy, r, end);
  const finish = polar(cx, cy, r, startAngle);
  const large = end - startAngle <= 180 ? 0 : 1;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 0 ${finish.x} ${finish.y}`;
}

function normalizeSlices(data: WebDonutSlice[], maxSlices: number): WebDonutSlice[] {
  const positive = data
    .filter((d) => d.amount > 0)
    .map((d) => ({ ...d, amount: Math.max(0, d.amount) }))
    .sort((a, b) => b.amount - a.amount);

  if (positive.length <= maxSlices) return positive;

  const head = positive.slice(0, maxSlices - 1);
  const tail = positive.slice(maxSlices - 1);
  const otherAmount = tail.reduce((s, d) => s + d.amount, 0);
  return [
    ...head,
    {
      name: 'Autres',
      amount: otherAmount,
      color: '#64748b',
    },
  ];
}

export default function DonutChart({
  data,
  size = 280,
  strokeWidth = 28,
  gapDegrees,
  centerPrimary,
  centerSecondary = 'Total des dépenses',
  maxSlices = 7,
}: Props) {
  const reactId = useId().replace(/:/g, '');
  const glowId = `donut-glow-${reactId}`;
  const softGlowId = `donut-soft-${reactId}`;

  const cx = size / 2;
  const cy = size / 2;
  const radius = (size - strokeWidth) / 2 - 4;

  // Avec strokeLinecap=round, chaque embout déborde d’≈ strokeWidth/2 le long de l’arc.
  // Il faut un gap ≥ à ce débordement des deux côtés, sinon les segments se chevauchent.
  const autoGap = useMemo(() => {
    const capDeg = ((strokeWidth / 2) / radius) * (180 / Math.PI);
    return Math.max(2.5, capDeg * 1.15);
  }, [strokeWidth, radius]);

  const gap = gapDegrees ?? autoGap;

  const slices = useMemo(() => normalizeSlices(data, maxSlices), [data, maxSlices]);

  const arcs = useMemo(() => {
    const total = slices.reduce((s, d) => s + d.amount, 0);
    if (total <= 0) {
      return [{ key: 'empty', d: arcPath(cx, cy, radius, 0, 359.9), color: 'rgba(255,255,255,0.08)', sweep: 359.9 }];
    }

    const n = slices.length;
    const usable = Math.max(1, 360 - gap * n);
    let angle = 0;
    const out: { key: string; d: string; color: string; sweep: number }[] = [];

    slices.forEach((slice, i) => {
      const sweep = (slice.amount / total) * usable;
      const start = angle + gap / 2;
      const end = start + sweep;
      out.push({
        key: `${slice.name ?? i}-${i}`,
        d: arcPath(cx, cy, radius, start, end),
        color: slice.color || '#64748b',
        sweep,
      });
      angle = end + gap / 2;
    });

    return out;
  }, [slices, cx, cy, radius, gap]);

  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="block overflow-visible"
        aria-hidden
      >
        <defs>
          {/* Glow doux unique (pas par segment) — évite le halo qui grossit les petites parts */}
          <filter id={softGlowId} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="1.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.35 0"
            />
          </filter>
        </defs>

        {/* Halo d’ambiance derrière l’anneau */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="rgba(0, 230, 118, 0.12)"
          strokeWidth={strokeWidth + 10}
          filter={`url(#${glowId})`}
        />

        {/* Piste de fond */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={strokeWidth}
        />

        <g filter={`url(#${softGlowId})`}>
          {arcs.map((a) => (
            <path
              key={a.key}
              d={a.d}
              fill="none"
              stroke={a.color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </g>
      </svg>

      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-9 text-center">
        <p
          className="font-bold tracking-tight text-white tabular-nums"
          style={{
            fontSize: 'clamp(1.05rem, 0.85rem + 0.9vw, 1.6rem)',
            lineHeight: 1.15,
            maxWidth: '100%',
            overflowWrap: 'anywhere',
          }}
        >
          {centerPrimary}
        </p>
        {centerSecondary && (
          <p className="mt-1.5 max-w-[11rem] text-body-sm leading-snug text-dark-400">
            {centerSecondary}
          </p>
        )}
      </div>
    </div>
  );
}
